import { DotFilledIcon } from '@radix-ui/react-icons';
import { Box, Flex, Skeleton, Text } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { DOT_RGB_GREEN, DOT_RGB_YELLOW } from 'classes/plate-canvas';
import { ErrorBoundary } from 'components/common/error-boundary';
import {
  DataCollectorContext,
  DataCollectorProvider,
} from 'components/machine/dialogs/installation/steps/data-collector/context';
import { DataCollectorEmptyHopperDetector } from 'components/machine/dialogs/installation/steps/data-collector/empty-hopper-detector';
import { DataCollectorPlateView } from 'components/machine/dialogs/installation/steps/data-collector/plate';
import { DataCollectorStatusBar } from 'components/machine/dialogs/installation/steps/data-collector/status-bar';
import { DataCollectorTrackingErrors } from 'components/machine/dialogs/installation/steps/data-collector/tracking-errors';
import { AimingProvider } from 'contexts/aiming.context';
import { MachineContext } from 'contexts/machine.context';
import { TrainingProvider } from 'contexts/training.context';
import { SubStatus } from 'enums/installation';
import { t } from 'i18next';
import { IVerboseComponent } from 'interfaces/i-components';
import { TrainingHelper } from 'lib_ts/classes/training.helper';
import { DataCollectionStep } from 'lib_ts/enums/data-collector.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  ICollectionShotGroup,
  IMachineShot,
} from 'lib_ts/interfaces/training/i-machine-shot';
import { clamp, clone, first } from 'lodash';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { DataCollectionsService as DCService } from 'services/data-collections.service';

const COMPONENT_NAME = 'DataCollector';

interface IProps extends IVerboseComponent {
  // data-collections > _id
  id: string;
  state: SubStatus;

  onComplete: () => void;
  updateFiringLog?: (agg: ICollectionShotGroup[]) => void;
  onToggleLowConfidence?: () => Promise<void>;
}

const ShotSummary = (props: {
  aiming: number;
  reqAiming: number;
  analysis: number;
  reqAnalysis: number;
  pitchIndex: number;
  totalPitches: number;
}) => {
  const isAiming = props.aiming < props.reqAiming;

  return (
    <Flex gap="1" align="center">
      <Text color={RADIX.COLOR.SECONDARY}>
        {`${t('common.pitch')} ${props.pitchIndex}/${props.totalPitches}`}
      </Text>

      <DotFilledIcon
        style={{ color: `rgb(${isAiming ? DOT_RGB_GREEN : DOT_RGB_YELLOW})` }}
      />

      {isAiming && (
        <Text>
          {`Aiming ${clamp(props.aiming, 1, props.reqAiming)}/${
            props.reqAiming
          }`}
        </Text>
      )}

      {!isAiming && (
        <Text>
          {`Analysis ${clamp(props.analysis, 1, props.reqAnalysis)}/${
            props.reqAnalysis
          }`}
        </Text>
      )}
    </Flex>
  );
};

export const DataCollectorHoC = (props: IProps) => {
  return (
    <AimingProvider identifier="DCAimingCx" training>
      <DataCollectorProvider
        id={props.id}
        onToggleLowConfidence={props.onToggleLowConfidence}
      >
        <DataCollector {...props} />
      </DataCollectorProvider>
    </AimingProvider>
  );
};

const DataCollector: FC<IProps> = (props) => {
  const { machine } = useContext(MachineContext);

  const { collection, setWaiting } = useContext(DataCollectorContext);

  const reqShots = useMemo(
    () => TrainingHelper.getDataCollectorShots(collection.type),
    [collection.type]
  );

  const [agg, setAgg] = useState<ICollectionShotGroup[]>([]);

  // load up the shots results at mount (e.g. resuming), or upon changes (e.g. LC toggled)
  useEffect(() => {
    if (!collection.collectionID) {
      // main still loading
      return;
    }

    const callback = async () => {
      const output = await DCService.getShotSummaries(collection._id);

      if (collection.refID) {
        const refShots = await DCService.getShotSummaries(collection.refID);

        // override aiming count w/ ref value since we'll be using those shots to aim
        output.forEach((m) => {
          const refShot = refShots.find(
            (rAgg) =>
              rAgg.pitch?._id === m.pitch?._id &&
              rAgg.matching_hash === m.matching_hash
          );

          if (!refShot) {
            console.warn(
              `Failed to find ref agg result for pitch ${
                m.pitch?._id ?? '???'
              } w/ matching hash ${m.matching_hash}`
            );
            return;
          }

          // i.e. the UI should skip trying to collect aiming shots for HWD
          m.aiming = refShot.aiming;
        });
      }

      setAgg(output);
    };

    callback();
  }, [machine.machineID, collection]);

  // the index of the first pitch that hasn't been skipped and there is insufficient data
  const activeIndex = useMemo(() => {
    if (!agg) {
      // wait until the aggregation query finishes instead of loading each one in quick succession
      return -1;
    }

    if (!collection.pitches) {
      // an old collection that did not save the pitches
      return -1;
    }

    const incompleteIndex = collection.pitches.findIndex((p, i) => {
      if (p.skipped) {
        return false;
      }

      const pitchAgg = agg.find((a) => a.pitch?._id === p._id);

      if (!pitchAgg) {
        verboseDebug(
          props,
          `Failed to find agg result for pitch ${i} (${p.name}, ${p._id})`,
          agg
        );
        return true;
      }

      if (pitchAgg.aiming < reqShots.aiming) {
        verboseDebug(
          props,
          `Insufficient aiming shots for pitch ${i} (${p.name}, ${p._id}); found ${pitchAgg.aiming}, needs ${reqShots.aiming}`
        );
        return true;
      }

      if (pitchAgg.analysis < reqShots.analysis) {
        verboseDebug(
          props,
          `Insufficient analysis shots for pitch ${i} (${p.name}, ${p._id}); found ${pitchAgg.analysis}, needs ${reqShots.analysis}`
        );
        return true;
      }

      // pitch has sufficient shots
      return false;
    });

    if (incompleteIndex !== -1) {
      // there is at least one pitch that isn't done yet
      verboseDebug(
        props,
        `Found incomplete pitch at ${incompleteIndex}`,
        collection.pitches[incompleteIndex]
      );
      return incompleteIndex;
    }

    // default to the final pitch
    return collection.pitches.length - 1;
  }, [collection.pitches, agg, reqShots]);

  const activePitch = useMemo(() => {
    const output = collection.pitches?.[activeIndex];
    verboseDebug(
      props,
      `Changing activePitch > ${output?.name} (${activeIndex})`
    );
    return output;
  }, [collection.pitches, activeIndex]);

  // update this whenever a successful training msg comes through to trigger a reload
  const [lastFetched, setLastFetched] = useState(Date.now());

  const [loadingShots, setLoadingShots] = useState(false);

  const [aimingShots, setAimingShots] = useState<IMachineShot[]>([]);
  const [analysisShots, setAnalysisShots] = useState<IMachineShot[]>([]);

  const allShots = useMemo(() => {
    if (!activePitch) {
      return [];
    }

    const activeHash = activePitch._mx_ms.matching_hash;

    const allShots = [...aimingShots, ...analysisShots];

    const activeShots = allShots.filter(
      (s) => s.target_ms.matching_hash === activeHash
    );

    return activeShots;
  }, [activePitch, machine, aimingShots, analysisShots]);

  // after aiming shots are reloaded, we aren't waiting for training data anymore
  useEffect(() => {
    setWaiting(false);
  }, [aimingShots]);

  useEffect(() => {
    const skipCallback = () => {
      setAimingShots([]);
      setAnalysisShots([]);
    };

    if (!collection.collectionID) {
      verboseDebug(props, 'Collection not loaded yet');
      skipCallback();
      return;
    }

    if (!activePitch) {
      verboseDebug(props, 'No active pitch');
      skipCallback();
      return;
    }

    const activeHash = activePitch._mx_ms.matching_hash;

    if (!activeHash) {
      verboseDebug(props, 'No hash for active pitch');
      skipCallback();
      return;
    }

    setLoadingShots(true);

    const callback = async () => {
      verboseDebug(props, {
        event: 'Loading shots from collection(s)',
        hash: activeHash,
        collectionID: collection.collectionID,
        refCollectionID: collection.refCollectionID,
      });

      const nextEntry: ICollectionShotGroup = {
        pitch: activePitch,
        matching_hash: activeHash,
        shots: [],
        total: 0,
        aiming: 0,
        analysis: 0,
      };

      // load the shots for the new active pitch
      const mainShots = first(
        await DCService.getShotSummaries(collection._id, activeHash)
      );

      const refShots = collection.refID
        ? // use aiming shots from the ref collection (if provided)
          first(await DCService.getShotSummaries(collection.refID, activeHash))
        : // use aiming shots from the main collection otherwise
          mainShots;

      if (refShots) {
        nextEntry.shots = refShots.shots;
      }

      const _aimingShots = refShots?.shots?.filter((m) => m.aiming) ?? [];
      nextEntry.aiming = _aimingShots.length;

      verboseDebug(
        props,
        `Fetched aiming shots from ${
          collection.refID ? 'ref' : 'main'
        } collection`,
        _aimingShots
      );

      // * IMPORTANT: this is necessary to trigger the effect in status bar, which skips the resend if the shot IDs haven't actually changed
      setAimingShots(_aimingShots);

      // analysis shots always come from the main collection
      const _analysisShots = mainShots?.shots.filter((m) => !m.aiming) ?? [];
      nextEntry.analysis = _analysisShots.length;

      verboseDebug(
        props,
        'Fetched analysis shots from main collection',
        _analysisShots
      );
      setAnalysisShots(_analysisShots);

      nextEntry.total = nextEntry.aiming + nextEntry.analysis;

      verboseDebug(
        props,
        `Updating agg entry for ${activePitch.name}`,
        nextEntry
      );
      setAgg((prev) => {
        const nextValue = clone(prev);

        const existingIndex = nextValue.findIndex(
          (p) => p.pitch?._id === nextEntry.pitch?._id
        );

        if (existingIndex === -1) {
          // first time
          nextValue.push(nextEntry);
        } else {
          nextValue.splice(existingIndex, 1, nextEntry);
        }

        return nextValue;
      });

      setLoadingShots(false);
    };

    callback();
  }, [machine.machineID, collection, activePitch, lastFetched]);

  const shotCount = useMemo(() => {
    if (activeIndex === -1 || !activePitch) {
      // avoid showing pitch 0 of X
      return 1;
    }

    const current =
      Math.min(reqShots.aiming, aimingShots.length) +
      Math.min(reqShots.analysis, analysisShots.length);

    return loadingShots ? 0 : current;
  }, [
    activeIndex,
    activePitch,
    loadingShots,
    aimingShots,
    analysisShots,
    reqShots.aiming,
    reqShots.analysis,
  ]);

  const overall = useMemo(() => {
    if (!collection.pitches) {
      return {
        shotsRemaining: 0,
        progress: 0,
      };
    }

    const safeActiveIndex = Math.max(0, activeIndex);

    // completed and skipped pitches are both considered passed
    const passed = safeActiveIndex * (reqShots.aiming + reqShots.analysis);

    const current =
      Math.min(reqShots.aiming, aimingShots.length) +
      Math.min(reqShots.analysis, analysisShots.length);

    const totalShotsCounted = passed + (loadingShots ? 0 : current);
    const totalShotsRequired =
      (reqShots.aiming + reqShots.analysis) * collection.pitches.length;

    return {
      shotsRemaining: totalShotsRequired - totalShotsCounted,
      progress: totalShotsCounted / totalShotsRequired,
    };
  }, [
    activeIndex,
    activePitch,
    loadingShots,
    aimingShots,
    analysisShots,
    reqShots,
  ]);

  const step = useMemo(() => {
    if (reqShots.aiming === 0 && reqShots.analysis === 0) {
      return DataCollectionStep.Loading;
    }

    const pitch = collection.pitches?.[activeIndex];

    if (!pitch) {
      return DataCollectionStep.Loading;
    }

    if (
      reqShots.aiming >
      aimingShots.filter((s) => s.pitch_id === pitch._id).length
    ) {
      return DataCollectionStep.AimingShots;
    }

    if (
      reqShots.analysis >
      analysisShots.filter((s) => s.pitch_id === pitch._id).length
    ) {
      return DataCollectionStep.AnalysisShots;
    }

    return DataCollectionStep.PitchComplete;
  }, [collection.pitches, reqShots, activeIndex, aimingShots, analysisShots]);

  const isComplete = useMemo(() => {
    if (!collection.pitches) {
      return false;
    }

    if (collection.pitches.length === 0) {
      return false;
    }

    const onLastPitch = activeIndex >= collection.pitches.length - 1;

    if (!onLastPitch) {
      // not on final pitch yet
      return false;
    }

    // we don't get a PitchComplete step if the last pitch is skipped
    const lastPitch = collection.pitches[activeIndex];
    return lastPitch.skipped || step === DataCollectionStep.PitchComplete;
  }, [collection.pitches, activeIndex, step]);

  // yuck but w/e
  useEffect(() => {
    if (!isComplete) {
      return;
    }

    props.onComplete();
  }, [isComplete]);

  // keep firing log in sync with the shots we've fired here
  useEffect(() => {
    props.updateFiringLog?.(agg);
  }, [agg]);

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      {/* provider used for training msg listener and failed shots warnings */}
      <TrainingProvider
        identifier="DataCollectorTrainingCx"
        mode={undefined}
        afterTrainingMsg={(msg) => {
          if (msg.success === undefined) {
            // only react to final training msgs
            return;
          }

          verboseDebug(props, 'Training msg received', msg);

          if (!msg.success) {
            // no need to reload shots since none was made, but we need to stop waiting so we can fire again
            setWaiting(false);
            return;
          }

          // reload shots which will also reset waiting to false
          setLastFetched(Date.now());
        }}
        ignoreAfOnTr
      >
        <Skeleton loading={!agg}>
          <Flex direction="column" align="center" style={{ width: '100%' }}>
            {isComplete && (
              <Box>{t('common.completed-data-collection-msg')}</Box>
            )}

            <DataCollectorPlateView
              state={props.state}
              complete={isComplete}
              shots={allShots}
            />

            <Flex direction="column" gap="2" style={{ width: '100%' }}>
              {activePitch && (
                <DataCollectorTrackingErrors pitch={activePitch} />
              )}

              {activePitch ? (
                <Box mt="2">
                  <DataCollectorStatusBar
                    isComplete={isComplete}
                    pitch={activePitch}
                    pitchIndex={activeIndex + 1}
                    totalPitches={collection.pitches?.length ?? 0}
                    shotCount={shotCount}
                    totalShots={reqShots.aiming + reqShots.analysis}
                    shotSummary={
                      <ShotSummary
                        pitchIndex={activeIndex + 1}
                        totalPitches={collection.pitches?.length ?? 0}
                        aiming={aimingShots.length}
                        reqAiming={reqShots.aiming}
                        analysis={analysisShots.length}
                        reqAnalysis={reqShots.analysis}
                      />
                    }
                    overallShotsRemaining={overall.shotsRemaining}
                    overallProgress={overall.progress}
                    aimingShots={aimingShots}
                    step={step}
                  />
                </Box>
              ) : (
                // mimic the status bar height while we're waiting for the active pitch to minimize layout shift
                <Skeleton height="88px" />
              )}

              <DataCollectorEmptyHopperDetector />
            </Flex>
          </Flex>
        </Skeleton>
      </TrainingProvider>
    </ErrorBoundary>
  );
};
